import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import VoiceIllustration from "@/components/VoiceIllustration";
import heroBg from "@/assets/hero-bg-new.png";
import { AnimatedSection } from "@/components/AnimatedSection";
import callIcon from "@/assets/Icons/Call.png";
import micIcon from "@/assets/Icons/Microphone.png";
import cogwheelIcon from "@/assets/Icons/Cogwheel.png";

const VoiceAssistant = () => {
  const { language } = useLanguage();

  const content = {
    is: {
      title: "RaddFulltrúi",
      subtitle: "Gervigreindardrifin raddaðstoð fyrir símaþjónustu",
      description: "RaddFulltrúi getur svarað símtölum, bókað tíma, og veitt upplýsingar með náttúrulegri málvinnslu og mannlegri rödd.",
      features: [
        {
          icon: micIcon,
          title: "Náttúruleg raddgreining",
          description: "Skilur og túlkar mannlegt tal með hárri nákvæmni á íslensku og ensku.",
        },
        {
          icon: callIcon,
          title: "Skalanleg símaþjónusta",
          description: "Getur tekið á móti tugum símtala samtímis án biðtíma og samþættist núverandi símakerfi.",
        },
        {
          icon: cogwheelIcon,
          title: "Sérsniðin fyrir þitt fyrirtæki",
          description: "Aðlagar svör og þjónustu eftir viðskiptavini og veitir persónulega upplifun.",
        },
      ],
    },
    en: {
      title: "Voice Assistant",
      subtitle: "AI-powered voice support for phone service",
      description: "Voice Assistant can answer calls, book appointments, and provide information with natural language processing and human-like voice.",
      features: [
        {
          icon: micIcon,
          title: "Natural Voice Recognition",
          description: "Understands and interprets human speech with high accuracy in Icelandic and English.",
        },
        {
          icon: callIcon,
          title: "Scalable Phone Service",
          description: "Handles dozens of simultaneous calls without wait times and integrates with existing phone systems.",
        },
        {
          icon: cogwheelIcon,
          title: "Customized for Your Business",
          description: "Adapts responses and service to customers and provides personalized experiences.",
        },
      ],
    },
  };

  const t = content[language];

  return (
    <main className="min-h-screen">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${heroBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-background/65 via-background/60 to-background" />
        </div>

        <div className="container px-6 md:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            {/* Replace lucide icon with PNG */}
            <img
              src={callIcon}
              alt={t.title}
              className="h-32 w-32 mx-auto mb-6 object-contain"
              loading="lazy"
            />
            <h1 className="mb-6">{t.title}</h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8">
              {t.subtitle}
            </p>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t.description}
            </p>
          </div>
        </div>
      </section>

      {/* Illustration Section */}
      <AnimatedSection>
        <section className="py-20">
          <div className="container px-6 md:px-8">
            <VoiceIllustration />
          </div>
        </section>
      </AnimatedSection>

      {/* Features Section */}
      <AnimatedSection delay={0.1}>
        <section className="py-20">
          <div className="container px-6 md:px-8">
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {t.features.map((feature, index) => (
                <div
                  key={index}
                  className="p-8 rounded-lg border border-border bg-card hover:shadow-lg transition-all animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <img
                    src={feature.icon}
                    alt={feature.title}
                    className="h-24 w-24 mb-4 object-contain"
                    loading="lazy"
                  />
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      <Footer />
    </main>
  );
};

export default VoiceAssistant;
